// Two-axis Journey (Dean, 13 Oct 2026): Account (LoanDisk) · Legal action (computed) — one line, same on every list.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const ACCOUNT_TONES = {
  open:         "bg-slate-100 text-slate-700 border-slate-300",
  arrears:      "bg-amber-50 text-amber-800 border-amber-300",
  legal:        "bg-rose-50 text-rose-800 border-rose-300",
  restructured: "bg-orange-50 text-orange-800 border-orange-300",
  closed:       "bg-emerald-50 text-emerald-800 border-emerald-300",
};
export const LEGAL_TONES = {
  pre_action:         "bg-sky-50 text-sky-800 border-sky-300",
  ready_for_handover: "bg-amber-100 text-amber-900 border-amber-400",
  live:               "bg-rose-50 text-rose-800 border-rose-300",
  paused:             "bg-slate-100 text-slate-600 border-slate-300",
  done:               "bg-emerald-50 text-emerald-800 border-emerald-300",
};
export const LANE_META = {
  handover:    { label: "Handover",    hint: "DN and LBA both expired — waiting to be handed to Legal" },
  chase:       { label: "Chase",       hint: "Notice out and Collections still chasing, or LoanDisk says Legal with nothing started yet" },
  legal_steps: { label: "Legal steps", hint: "Live legal action — route or ladder stage in progress" },
  paused:      { label: "Paused",      hint: "Legal action paused — plan agreed, Breathing Space or a review date" },
};

const pill = "inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap";
const fmtDate = (iso) => (iso ? new Date(iso.length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");

export function JourneyChips({ journey, caseId, hideAccount = false, hideLegal = false }) {
  if (!journey || (hideAccount && hideLegal)) return null;
  const acc = journey.account || {}, leg = journey.legal || {};
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`journey-chips-${caseId}`}>
      {!hideAccount && <span className={`${pill} ${ACCOUNT_TONES[acc.key] || ACCOUNT_TONES.open}`} data-testid={`journey-account-${caseId}`}>{acc.key === "open" ? "Open loan" : acc.label}</span>}
      {!hideLegal && leg.key && leg.key !== "none" && (
        <span className={`${pill} ${LEGAL_TONES[leg.key] || LEGAL_TONES.live}`} data-testid={`journey-legal-${caseId}`}>⚖ {leg.label}</span>
      )}
    </span>
  );
}

function JourneyHover({ journey }) {
  const acc = journey.account || {}, leg = journey.legal || {}, own = journey.owners || {}, na = journey.next_action;
  const attention = journey.attention || [];
  return (
    <div className="space-y-1.5 max-w-sm text-xs">
      <div className="font-semibold">{journey.line}</div>
      <div><span className="text-slate-400">Account:</span> {acc.label}{acc.ld_status_id != null ? ` · LoanDisk status ${acc.ld_status_id}` : acc.ld_text ? ` · LoanDisk "${acc.ld_text}"` : ""}{acc.dpd ? ` · ${acc.dpd} DPD` : ""}{acc.on_report ? " · on today's arrears report" : ""}</div>
      <div><span className="text-slate-400">Legal action:</span> {leg.label}{leg.detail ? ` — ${leg.detail}` : ""}</div>
      <div><span className="text-slate-400">Lists:</span> {[journey.on_worklist && "Collections worklist", journey.on_tracker && "Legal tracker"].filter(Boolean).join(" · ") || "neither (closed history)"}</div>
      <div><span className="text-slate-400">Owners:</span> account {own.account || "—"} · legal {own.legal || "—"}</div>
      {na && <div><span className="text-slate-400">Next action:</span> {na.label} · {fmtDate(na.date)}{na.overdue_days > 0 ? ` · ${na.overdue_days}d overdue` : ""}</div>}
      {journey.promise && <div><span className="text-slate-400">Promise:</span> {journey.promise.label}{journey.promise.amount ? ` · £${Number(journey.promise.amount).toLocaleString("en-GB")}` : ""}</div>}
      {attention.length > 0 && (
        <ul className="border-t border-slate-600 pt-1 space-y-0.5">
          {attention.map((a) => <li key={a.key} className="text-amber-200">⚠ {a.reason}</li>)}
        </ul>
      )}
    </div>
  );
}

export function JourneyLine({ journey, caseId, hideAccount = false, hideLegal = false, className = "" }) {
  if (!journey) return null;
  const attention = journey.attention || [];
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`min-w-0 cursor-help ${className}`} data-testid={`journey-line-${caseId}`}>
            <JourneyChips journey={journey} caseId={caseId} hideAccount={hideAccount} hideLegal={hideLegal} />
            {journey.sub && <div className="text-[10px] text-slate-600 truncate max-w-[260px] mt-0.5" data-testid={`journey-sub-${caseId}`}>{journey.sub}</div>}
            {attention.length > 0 && (
              <div className="text-[10px] text-amber-700 truncate max-w-[260px]" data-testid={`journey-attention-${caseId}`}>
                ⚠ {attention[0].label}{attention.length > 1 ? ` +${attention.length - 1}` : ""}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="text-xs"><JourneyHover journey={journey} /></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
